import { ref, watch } from 'vue'
import { asyncComputed, createGlobalState } from '@vueuse/core'
import { useRouter } from 'vue-router'

import { useClient } from '@/opendoors-api/useClient'
import { userMe, userUpdateProfile } from '@/opendoors-api/client'
import type { Language } from '@/opendoors-api/client/types.gen'
import { useAuth } from '@/auth/useAuth'
import { AppCancelError } from '@/errors'
import { useAppStartup } from '@/composables/useAppStartup'

export const useUser = createGlobalState(() => {
  const { isAuthenticated } = useAuth()
  const { reportNetworkError } = useAppStartup()
  const { client } = useClient()
  const loading = ref(true)
  const updateUserKey = ref(1)
  const router = useRouter()

  const getUser = async (signal) => {
    if (isAuthenticated.value) {
      const { data, error, response } = await userMe({
        client: client.value,
        throwOnError: false,
        signal: signal
      })
      if (response?.status === 403) {
        await router.push('/unauthorizedError')
        return undefined
      } else if (error) {
        if (reportNetworkError(error, response, signal)) {
          return undefined
        }
        throw error
      }
      return data
    }
    return undefined
  }

  const user = asyncComputed(
    async (cancel) => {
      const abortController = new AbortController()
      cancel(() => {
        abortController.abort(new AppCancelError('user state recomputed'))
      })
      if (!updateUserKey.value) {
        return null
      }
      const data = await getUser(abortController.signal)
      return data
    },
    null,
    {
      evaluating: loading,
      lazy: false,
      flush: 'sync'
    }
  )

  const setLanguage = async (localeCode: Language = 'nl') => {
    await userUpdateProfile({
      client: client.value,
      body: {
        preferredLanguage: localeCode
      },
      throwOnError: true
    })
    updateUserKey.value++
  }

  watch(
    [client, isAuthenticated],
    async ([newClient, isAuth]) => {
      if (newClient && isAuth) {
        updateUserKey.value++
      }
    },
    { immediate: true }
  )

  return { user, setLanguage, loading }
})
